/**
 * Creator Studio — Main Application Store
 *
 * Manages projects, templates, and the active canvas state.
 * Uses Zustand for state management.
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { generateAndSaveThumbnail, generateAndSaveTemplateThumbnail } from '../lib/thumbnailGenerator';
import { useAuthStore } from './authStore';
import { useMappingStore } from './mappingStore';
import { resolveMappedValueFromBrandData, buildEavBrandDataKey } from '../lib/mappingResolver';
import { guardCanvasStateForSave, MAX_CANVAS_STATE_BYTES } from '../lib/canvasStateGuard';
import { expandSubtree, cloneSubtreeWithNewIds, pruneChildIdFromGroups } from '../lib/groupUtils';
import { findVariantForTriple, getFontVariantById } from '../lib/fontLoader';
import { measureTextBlockHeight } from '../lib/textMetrics';
import { useFontStore } from './fontStore';
import { useCanvasStore } from './canvasStore';
import { getDefaultButtonFont } from '../lib/defaults';
import type {
  Project,
  ProjectStatus,
  Template,
  CanvasState,
  CanvasElement,
  CanvasDimensions,
  ExportJob,
  SchoolBrand,
  School,
  GroupElement,
  SvgElement,
} from '../types';

function serializeMappingsForSave(
  elementMappings: Record<string, Array<{
    id: string;
    source: string;
    tableName: string;
    columnName: string;
    columnDataType: string;
    targetProperty: string;
    fallbackValue?: string;
    transformExpression?: string | null;
    filterColumn?: string | null;
    filterValue?: string | null;
    filterCategory?: string | null;
  }>>
): Array<Record<string, unknown>> {
  const rows: Array<Record<string, unknown>> = [];
  for (const [elementId, mappings] of Object.entries(elementMappings)) {
    mappings.forEach((m, idx) => {
      if (!m.tableName || !m.columnName || !m.targetProperty) return;
      rows.push({
        id: m.id,
        element_id: elementId,
        source: m.source || 'ad',
        table_name: m.tableName,
        column_name: m.columnName,
        column_data_type: m.columnDataType || 'text',
        target_property: m.targetProperty,
        fallback_value: m.fallbackValue ?? '',
        transform_expression: m.transformExpression ?? null,
        sort_order: idx,
        filter_column: m.filterColumn ?? null,
        filter_value: m.filterValue ?? null,
        filter_category: m.filterCategory ?? null,
      });
    });
  }
  return rows;
}

function deriveTextElementName(text: string): string {
  if (!text || text.trim() === '') return 'Text';
  const cleaned = text.trim().replace(/\n/g, ' ');
  if (cleaned.length <= 30) return cleaned;
  const truncated = cleaned.substring(0, 30);
  const lastSpace = truncated.lastIndexOf(' ');
  if (lastSpace > 15) return truncated.substring(0, lastSpace) + '\u2026';
  return truncated + '\u2026';
}

// Text elements intentionally do not carry a user-editable name. The layer
// name and Property Panel "Name" field always mirror the rendered text via
// deriveTextElementName. Editing the text re-derives the name; duplicates
// start with "<derived> (copy)" and revert to "<newText>" as soon as the
// copy's text is edited. See otto_docs: text-layer-name-mirrors-text.

interface StudioState {
  projects: Project[];
  activeProject: Project | null;
  projectsLoading: boolean;

  templates: Template[];
  activeTemplate: Template | null;
  templatesLoading: boolean;

  canvasState: CanvasState;
  selectedElementId: string | null;
  selectedElementIds: string[];
  clipboardElement: CanvasElement | null;
  undoStack: CanvasState[];
  redoStack: CanvasState[];

  exportJobs: ExportJob[];
  exportsLoading: boolean;

  schools: School[];
  schoolBrands: SchoolBrand[];
  schoolsLoading: boolean;

  editAsBrandId: string | null;
  editAsBrandData: Record<string, unknown> | null;
  editAsLoading: boolean;

  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  lastSavedAt: Date | null;
  saveError: string | null;
  _autoSaveTimer: ReturnType<typeof setTimeout> | null;
  _thumbnailTimer: ReturnType<typeof setTimeout> | null;
  _lastSavedElementCount: number;
  _isSaving: boolean;

  // Project collaboration / locking
  lockState: { status: 'idle' | 'acquiring' | 'held' | 'blocked'; heldBy: string | null; heldByName: string | null; heldAt: string | null };
  _lockHeartbeat: ReturnType<typeof setInterval> | null;
  projectOwners: Record<string, { name: string; email: string }>;
  thumbnailLastError: string | null;
  acquireProjectLock: (projectId: string) => Promise<{ acquired: boolean; heldBy?: string; heldAt?: string }>;
  releaseProjectLock: () => Promise<void>;
  forceReleaseProjectLock: (projectId: string) => Promise<boolean>;

  loadProjects: () => Promise<void>;
  createProject: (name: string, description: string, dimensions: CanvasDimensions, presetKey?: string) => Promise<Project | null>;
  updateProject: (id: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  duplicateProject: (id: string) => Promise<Project | null>;
  renameProject: (id: string, newName: string) => Promise<void>;
  setActiveProject: (project: Project | null) => void;
  loadFullProject: (id: string) => Promise<Project | null>;

  setCanvasState: (state: CanvasState) => void;
  setCanvasDimensions: (width: number, height: number) => void;
  addElement: (element: CanvasElement) => void;
  updateElement: (id: string, updates: Partial<CanvasElement>) => void;
  updateElements: (updates: Array<{ id: string; changes: Partial<CanvasElement> }>) => void;
  removeElement: (id: string) => void;
  selectElement: (id: string | null) => void;
  setSelectedElements: (ids: string[]) => void;
  addToSelection: (id: string) => void;
  removeFromSelection: (id: string) => void;
  clearSelection: () => void;
  getSelectedElements: () => CanvasElement[];
  duplicateElement: (id: string) => void;
  reorderElement: (id: string, direction: 'up' | 'down' | 'top' | 'bottom') => void;
  reorderElements: (ids: string[], direction: 'front' | 'back' | 'forward' | 'backward') => void;
  groupSelected: () => void;
  ungroupSelected: () => void;
  mergeSelectedToSvg: () => void;
  explodeSvgElement: (elementId?: string) => Promise<void>;
  restoreElements: (elements: CanvasElement[]) => void;
  /** Persist the accessible reading order (ordered text/button element IDs)
   *  into canvasState.readingOrder and schedule autosave. Not part of the
   *  undo stack — reading order is export metadata, not canvas edit state. */
  setReadingOrder: (order: string[]) => void;

  undo: () => void;
  redo: () => void;
  pushUndoState: () => void;

  _scheduleAutoSave: () => void;
  _executeAutoSave: () => Promise<void>;
  saveProjectNow: () => Promise<void>;
  _scheduleThumbnailGeneration: () => void;
  _generateThumbnail: () => Promise<void>;
  flushAndCleanup: () => void;

  loadTemplates: () => Promise<void>;
  saveAsTemplate: (name: string, description: string) => Promise<Template | null>;

  loadExportJobs: () => Promise<void>;

  loadSchools: () => Promise<void>;
  loadSchoolBrands: (schoolIds?: string[]) => Promise<void>;

  setEditAsBrand: (brandId: string | null) => Promise<void>;
  clearEditAs: () => void;
  getResolvedElementValue: (element: CanvasElement, property: string) => string | null;
  ensureEditAsTable: (source: string, tableName: string) => Promise<void>;
}

const DEFAULT_CANVAS_STATE: CanvasState = {
  dimensions: { width: 1200, height: 400 },
  backgroundColor: '#1a1a2e',
  elements: [],
};

const MAX_UNDO_STACK = 50;

export const useStudioStore = create<StudioState>((set, get) => ({
  projects: [],
  activeProject: null,
  projectsLoading: false,

  templates: [],
  activeTemplate: null,
  templatesLoading: false,

  canvasState: { ...DEFAULT_CANVAS_STATE },
  selectedElementId: null,
  selectedElementIds: [],
  clipboardElement: null,
  undoStack: [],
  redoStack: [],

  exportJobs: [],
  exportsLoading: false,

  schools: [],
  schoolBrands: [],
  schoolsLoading: false,

  editAsBrandId: null,
  editAsBrandData: null,
  editAsLoading: false,

  saveStatus: 'idle',
  lastSavedAt: null,
  saveError: null,
  _autoSaveTimer: null,
  _thumbnailTimer: null,
  _lastSavedElementCount: 0,
  _isSaving: false,

  lockState: { status: 'idle', heldBy: null, heldByName: null, heldAt: null },
  _lockHeartbeat: null,
  projectOwners: {},
  thumbnailLastError: null,

  acquireProjectLock: async (projectId: string) => {
    const { data, error } = await supabase.rpc('cs_acquire_project_lock', {
      p_project_id: projectId,
    });
    if (error) {
      console.warn('[Lock] acquire failed', error);
      return { acquired: false };
    }
    const result = (data || {}) as {
      acquired?: boolean;
      locked_by?: string;
      locked_at?: string;
      reason?: string;
    };
    if (result.acquired) {
      set({
        lockState: {
          status: 'held',
          heldBy: result.locked_by || null,
          heldByName: null,
          heldAt: result.locked_at || null,
        },
      });
      const existing = get()._lockHeartbeat;
      if (existing) clearInterval(existing);
      const heartbeat = setInterval(() => {
        supabase.rpc('cs_acquire_project_lock', { p_project_id: projectId }).then(
          () => {},
          (err) => { console.warn('[Lock] heartbeat failed', err); }
        );
      }, 60_000);
      set({ _lockHeartbeat: heartbeat });
      return { acquired: true };
    }

    let heldByName: string | null = null;
    if (result.locked_by) {
      const { data: who } = await supabase
        .from('tb_users')
        .select('full_name, display_name, email')
        .eq('id', result.locked_by)
        .maybeSingle();
      const u = who as { full_name?: string; display_name?: string; email?: string } | null;
      heldByName = u?.display_name || u?.full_name || u?.email || 'another user';
    }
    set({
      lockState: {
        status: 'blocked',
        heldBy: result.locked_by || null,
        heldByName,
        heldAt: result.locked_at || null,
      },
    });
    return { acquired: false, heldBy: result.locked_by, heldAt: result.locked_at };
  },

  releaseProjectLock: async () => {
    const state = get();
    const beat = state._lockHeartbeat;
    if (beat) clearInterval(beat);
    set({ _lockHeartbeat: null, lockState: { status: 'idle', heldBy: null, heldByName: null, heldAt: null } });
    const pid = state.activeProject?.id;
    if (!pid) return;
    try {
      await supabase.rpc('cs_release_project_lock', { p_project_id: pid });
    } catch (err) {
      console.warn('[Lock] release failed', err);
    }
  },

  forceReleaseProjectLock: async (projectId: string) => {
    const { data, error } = await supabase.rpc('cs_force_release_project_lock', {
      p_project_id: projectId,
    });
    if (error) {
      console.warn('[Lock] force-release failed', error);
      return false;
    }
    return data === true;
  },

  // ═══════════════════════════════════════
  // Project Actions
  // ═══════════════════════════════════════

  loadProjects: async () => {
    set({ projectsLoading: true });
    try {
      // Refresh project grants so canEdit / canDelete reflect the latest
      // collaborator rows for the current user before we render the cards.
      try {
        await useAuthStore.getState().loadProjectGrants();
      } catch { /* non-critical */ }

      const { data, error } = await supabase
        .from('cs_projects')
        .select('id, name, description, status, thumbnail_url, preset_key, tags, created_by, created_at, updated_at')
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('[Studio] Error loading projects:', error);
        return;
      }

      set({ projects: (data || []) as Project[] });
    } catch (err) {
      console.error('[Studio] Exception loading projects:', err);
    } finally {
      set({ projectsLoading: false });
    }
  },

  createProject: async (name, description, dimensions, presetKey) => {
    const newCanvasState: CanvasState = {
      dimensions,
      backgroundColor: '#1a1a2e',
      elements: [],
    };

    try {
      const { data, error } = await supabase
        .from('cs_projects')
        .insert({
          name,
          description,
          status: 'draft' as ProjectStatus,
          canvas_state: newCanvasState,
          preset_key: presetKey || null,
          tags: [],
        })
        .select()
        .single();

      if (error) {
        console.error('[Studio] Error creating project:', error);
        return null;
      }

      const project = data as Project;
      set((state) => ({
        projects: [project, ...state.projects],
        activeProject: project,
        canvasState: newCanvasState,
        selectedElementId: null,
        selectedElementIds: [],
        undoStack: [],
        redoStack: [],
      }));

      return project;
    } catch (err) {
      console.error('[Studio] Exception creating project:', err);
      return null;
    }
  },

  updateProject: async (id, updates) => {
    try {
      const { error } = await supabase
        .from('cs_projects')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        console.error('[Studio] Error updating project:', error);
        return;
      }

      set((state) => ({
        projects: state.projects.map((p) =>
          p.id === id ? { ...p, ...updates, updated_at: new Date().toISOString() } : p
        ),
        activeProject:
          state.activeProject?.id === id
            ? { ...state.activeProject, ...updates, updated_at: new Date().toISOString() }
            : state.activeProject,
      }));
    } catch (err) {
      console.error('[Studio] Exception updating project:', err);
    }
  },

  deleteProject: async (id) => {
    try {
      try {
        await supabase.storage
          .from('cs-assets')
          .remove([`thumbnails/${id}.jpg`]);
      } catch (storageErr) {
        console.warn('[Studio] Project thumbnail cleanup skipped:', storageErr);
      }

      const { error } = await supabase
        .from('cs_projects')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('[Studio] Error deleting project:', error);
        return;
      }

      set((state) => ({
        projects: state.projects.filter((p) => p.id !== id),
        activeProject: state.activeProject?.id === id ? null : state.activeProject,
      }));
    } catch (err) {
      console.error('[Studio] Exception deleting project:', err);
    }
  },

  duplicateProject: async (id) => {
    try {
      // listProjects omits canvas_state from its select, so the in-memory
      // `projects` entry has no elements on it. Duplication must go through
      // the server-side RPC, which reads the full source row under RLS,
      // inserts a deep copy, and clones cs_element_mappings in the same
      // transaction so dynamic bindings survive the duplicate.
      const { data, error } = await supabase.rpc('cs_duplicate_project', {
        p_source_id: id,
      });

      if (error) {
        console.error('[Studio] Error duplicating project:', error);
        return null;
      }

      const row = Array.isArray(data) ? data[0] : data;
      if (!row) {
        console.error('[Studio] Duplicate RPC returned empty payload');
        return null;
      }

      const project = row as Project;
      const sourceElementCount = Array.isArray(
        (project.canvas_state as { elements?: unknown[] } | null)?.elements
      )
        ? (project.canvas_state as { elements: unknown[] }).elements.length
        : 0;
      console.log('[Studio] Duplicated project', {
        sourceId: id,
        newId: project.id,
        elementCount: sourceElementCount,
      });

      // Best-effort thumbnail copy so the new project card shows a preview
      // immediately. A failure here is non-fatal: the thumbnail regenerates
      // on the first save of the duplicate.
      try {
        const { data: srcBlob } = await supabase.storage
          .from('cs-assets')
          .download(`thumbnails/${id}.jpg`);
        if (srcBlob) {
          await supabase.storage
            .from('cs-assets')
            .upload(`thumbnails/${project.id}.jpg`, srcBlob, {
              contentType: 'image/jpeg',
              upsert: true,
            });
        }
      } catch (thumbErr) {
        console.warn('[Studio] Thumbnail copy skipped for duplicate:', thumbErr);
      }

      set((state) => ({ projects: [project, ...state.projects] }));
      return project;
    } catch (err) {
      console.error('[Studio] Exception duplicating project:', err);
      return null;
    }
  },

  renameProject: async (id, newName) => {
    try {
      const { error } = await supabase
        .from('cs_projects')
        .update({ name: newName, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        console.error('[Studio] Error renaming project:', error);
        throw error;
      }

      set((state) => ({
        projects: state.projects.map((p) =>
          p.id === id ? { ...p, name: newName, updated_at: new Date().toISOString() } : p
        ),
        activeProject:
          state.activeProject?.id === id
            ? { ...state.activeProject, name: newName, updated_at: new Date().toISOString() }
            : state.activeProject,
      }));
    } catch (err) {
      console.error('[Studio] Exception renaming project:', err);
      throw err;
    }
  },

  setActiveProject: (project) => {
    const state = get();
    if (state._autoSaveTimer) clearTimeout(state._autoSaveTimer);
    if (state._thumbnailTimer) clearTimeout(state._thumbnailTimer);

    const rawCs = project?.canvas_state || { ...DEFAULT_CANVAS_STATE };
    // Legacy repair for Phase 11B textbox width policy: any text element saved before
    // widthIntent existed gets 'manual' as its default so its stored width is preserved.
    // The on-canvas sync still applies the floor (expands too-narrow boxes to fit text)
    // so older projects with clipped glyphs like "Bookstore" auto-heal on open.
    const cs = rawCs.elements
      ? {
          ...rawCs,
          elements: rawCs.elements.map((el) => {
            if (el.type === 'button') console.log('[Phase F.1.2] setActiveProject pre-repair button', { id: el.id, width: el.width, fontSize: el.fontSize, fontFamily: el.fontFamily, paddingX: (el as { paddingX?: number }).paddingX, text: (el as { text?: string }).text });
            let next: typeof el = el;
            // Text / button layer name always mirrors the rendered text.
            // Repairs legacy projects where editing a duplicated text left the
            // stale "tytyut copy" name behind (Ross Font Test). Idempotent.
            if (next.type === 'text' || next.type === 'button') {
              const t = (next as { text?: string }).text || '';
              const derived = deriveTextElementName(t);
              if ((next.name || '') !== derived) {
                next = { ...next, name: derived } as typeof el;
              }
            }
            // Legacy repair for Phase 11B textbox width policy.
            if (
              next.type === 'text' &&
              (next as { widthIntent?: unknown }).widthIntent === undefined
            ) {
              next = { ...next, widthIntent: 'manual' } as typeof el;
            }
            // Legacy repair for textFitMode vocabulary unification (Build Prompt B).
            // Per cs-cwt-cws-spec-lock §1, legacy values 'grow' | 'commit' | 'contain'
            // are translated to canonical 'natural' | 'contain-within-textbox' | 'contain-within-textbox'.
            // Both legacy CWT-ish values collapse to 'contain-within-textbox' because legacy
            // code did not distinguish CWT from CWS — only CWT was implemented. After this
            // one-shot repair, the runtime never reads legacy values again.
            if (next.type === 'text') {
              const legacyMode = (next as { textFitMode?: unknown }).textFitMode;
              if (legacyMode === 'grow') {
                next = { ...next, textFitMode: 'natural' } as typeof el;
              } else if (legacyMode === 'commit' || legacyMode === 'contain') {
                next = { ...next, textFitMode: 'contain-within-textbox' } as typeof el;
              }
            }
            // Phase F.1 self-heal: if button fontFamily is not in active cs_fonts,
            // swap to system default. Repairs legacy buttons created before Phase F.1
            // that may have 'Inter' or other non-cs_fonts values. Per CS Rule B.1.
            if (next.type === 'button') {
              const currentFonts = useFontStore.getState().fonts.filter((f) => f.isActive);
              const familyValid = currentFonts.some((f) => f.fontFamily === (next as { fontFamily?: string }).fontFamily);
              if (!familyValid) {
                const systemDefault = getDefaultButtonFont();
                if (systemDefault) {
                  next = {
                    ...next,
                    fontFamily: systemDefault.fontFamily,
                    fontVariantId: systemDefault.fontVariantId,
                    fontWeight: systemDefault.fontWeight,
                    fontStyle: systemDefault.fontStyle === 'italic' ? 'italic' : 'normal',
                  } as typeof el;
                }
              }
            }
            // Legacy repair for variant-as-first-class fonts: any text/button
            // element without `fontVariantId` gets its closest matching
            // cs_fonts row attached so the canvas, export, and editor all
            // agree on which file to render. Weight/style snap to the
            // variant to eliminate browser-synthesized bold/italic.
            if (next.type === 'text' || next.type === 'button') {
              const hasId =
                typeof (next as { fontVariantId?: unknown }).fontVariantId === 'string' &&
                (next as { fontVariantId?: string }).fontVariantId!.length > 0;
              if (!hasId) {
                const variant = findVariantForTriple(
                  (next as { fontFamily?: string }).fontFamily || '',
                  (next as { fontWeight?: number | string }).fontWeight,
                  (next as { fontStyle?: string }).fontStyle
                );
                if (variant) {
                  next = {
                    ...next,
                    fontVariantId: variant.id,
                    fontFamily: variant.fontFamily,
                    fontWeight: parseInt(variant.fontWeight, 10) || 400,
                    fontStyle: variant.fontStyle === 'italic' ? 'italic' : 'normal',
                  } as typeof el;
                }
              }
            }
            // Variant/Weight Reconciliation — restores integrity when fontVariantId disagrees with fontWeight/fontStyle.
            if (next.type === 'text' || next.type === 'button') {
              const variantId = (next as any).fontVariantId as string | undefined;
              const storedWeight = (next as any).fontWeight;
              const storedStyle = (next as any).fontStyle;
              if (variantId) {
                const variant = getFontVariantById(variantId);
                if (variant) {
                  const variantWeight = parseInt(variant.fontWeight, 10);
                  const variantStyle = variant.fontStyle === 'italic' ? 'italic' : 'normal';
                  const weightMismatch = Number.isFinite(variantWeight) && variantWeight !== Number(storedWeight);
                  const styleMismatch = variantStyle !== storedStyle;
                  if (weightMismatch || styleMismatch) {
                    console.warn(
                      `[Variant Reconcile] Element ${(next as any).id} (${(next as any).name || (next as any).type}): ` +
                      `stored weight/style (${storedWeight}/${storedStyle}) differs from variant ` +
                      `(${variantWeight}/${variantStyle}). Updating to match variant.`
                    );
                    next = { ...next, fontWeight: variantWeight, fontStyle: variantStyle } as typeof el;
                  }
                }
              }
            }
            // Phase F.2 Button Geometry Migration — Lazy.
            // Phase F.2 added originX:'center' to FabricGroup, causing handleObjectModified to write
            // the center X into element.x. Translate to top-left on first open after fix.
            if (next.type === 'button') {
              const geomVersion = (next as any)._geometryVersion;
              if (!geomVersion || geomVersion < 1) {
                const w = Number((next as any).width) || 0;
                const h = Number((next as any).height) || 0;
                if (w > 0 && h > 0) {
                  const newX = Number((next as any).x) - (w / 2);
                  const newY = Number((next as any).y) - (h / 2);
                  console.warn(
                    `[Geometry Migration] Button ${(next as any).id} (${(next as any).name}): ` +
                    `translated from center-origin to top-left-origin. ` +
                    `new x=${newX}, y=${newY}.`
                  );
                  next = { ...next, x: newX, y: newY, _geometryVersion: 1 } as typeof el;
                }
              }
            }
            // Clipped-width self-heal: legacy text elements saved under the
            // old too-tight padding rule may have widths below the minimum
            // needed to render without clipping. We ONLY run this for
            // elements that have never had an explicit `widthIntent` recorded
            // — a `manual` element is sacred, it reflects the user's chosen
            // width and must never be flipped on reload. We also require the
            // specific font variant to already be available in the document;
            // if the webfont hasn't loaded yet `ctx.measureText` falls back
            // to Arial and mis-measures condensed/display faces, which used
            // to trigger spurious `auto` flips and re-wrap the text on next
            // save.
            if (next.type === 'text') {
              const t = next as unknown as {
                text?: string;
                fontSize?: number;
                fontFamily?: string;
                fontWeight?: number | string;
                fontStyle?: string;
                width?: number;
                widthIntent?: 'auto' | 'manual';
              };
              const width = t.width ?? 0;
              const fontSize = t.fontSize ?? 0;
              const hasIntent = t.widthIntent === 'auto' || t.widthIntent === 'manual';
              if (!hasIntent && fontSize > 0 && width > 0 && t.text) {
                try {
                  const fw = t.fontWeight ?? 'normal';
                  const fs = t.fontStyle ?? 'normal';
                  const ff = t.fontFamily || 'Arial';
                  const fontSpec = `${fs} ${fw} ${fontSize}px "${ff}"`;
                  const fontsApi = (document as unknown as { fonts?: { check: (s: string) => boolean } }).fonts;
                  const faceReady = fontsApi ? fontsApi.check(fontSpec) : false;
                  if (faceReady) {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                      ctx.font = fontSpec;
                      let maxAdvance = 0;
                      for (const line of String(t.text).split('\n')) {
                        const adv = ctx.measureText(line || 'M').width;
                        if (adv > maxAdvance) maxAdvance = adv;
                      }
                      const pad = Math.max(fontSize * 0.05, 2);
                      const tight = maxAdvance + pad;
                      if (width + 1 < tight) {
                        next = { ...next, widthIntent: 'auto' } as typeof el;
                      } else if (width > tight + fontSize * 1.0) {
                        next = { ...next, widthIntent: 'auto' } as typeof el;
                      }
                    }
                  }
                } catch {
                  // measurement not available; leave widthIntent alone
                }
              }
            }
            // Descender-safe height self-heal for legacy text/button elements.
            // Pre-fix projects stored Fabric's line-box height, which clips the
            // descender on 'g', 'j', 'p', 'q', 'y' during overlay export.
            // Grow (never shrink) the stored height to include the real
            // font descent so future exports don't clip.
            if (next.type === 'text' || next.type === 'button') {
              const isManualText = next.type === 'text' && (next as any).widthIntent === 'manual';
              if (isManualText) {
                // Manual-intent text: designer sized the box deliberately; skip height self-heal.
              } else {
              const t = next as unknown as {
                text?: string;
                fontSize?: number;
                fontFamily?: string;
                fontWeight?: number | string;
                fontStyle?: string;
                lineHeight?: number;
                height?: number;
              };
              if (t.text && t.fontSize && t.fontSize > 0) {
                try {
                  const fw = t.fontWeight ?? 400;
                  const fs = t.fontStyle ?? 'normal';
                  const ff = t.fontFamily || 'Arial';
                  const fontSpec = `${fs} ${fw} ${t.fontSize}px "${ff}"`;
                  const fontsApi = (document as unknown as { fonts?: { check: (s: string) => boolean } }).fonts;
                  const faceReady = fontsApi ? fontsApi.check(fontSpec) : false;
                  if (faceReady) {
                    const measured = measureTextBlockHeight({
                      lines: String(t.text).split('\n'),
                      fontSize: t.fontSize,
                      fontFamily: ff,
                      fontWeight: fw,
                      fontStyle: String(fs),
                      lineHeight: t.lineHeight ?? 1.16,
                    });
                    const stored = t.height ?? 0;
                    if (measured.height > stored + 1) {
                      next = { ...next, height: measured.height } as typeof el;
                    }
                  }
                } catch {
                  // measurement unavailable; leave height alone
                }
              }
              } // end else (non-manual text)
            }
            // Advance-width self-heal for legacy text/button elements.
            // Buttons had no width fit policy before Phase 12; text had one
            // but with a 2px epsilon that could fail on trailing punctuation
            // (the "bookstore re-imagined." period clip). Grow (never shrink)
            // the stored width so the glyphs always sit inside the box.
            // Phase F.1.1: Post-F.1 buttons store authoritative width at
            // creation with paddingX baked in; skip self-heal for those.
            if (next.type === 'text' || next.type === 'button') {
              const t = next as unknown as {
                text?: string;
                fontSize?: number;
                fontFamily?: string;
                fontWeight?: number | string;
                fontStyle?: string;
                paddingX?: number;
                border?: { width?: number };
                width?: number;
                widthIntent?: 'auto' | 'manual';
              };
              const isPostF1Button = next.type === 'button' && typeof t.paddingX === 'number';
              const isManualWidthText = next.type === 'text' && t.widthIntent === 'manual';
              if (!isPostF1Button && !isManualWidthText && t.text && t.fontSize && t.fontSize > 0) {
                try {
                  const fw = t.fontWeight ?? 400;
                  const fs = t.fontStyle ?? 'normal';
                  const ff = t.fontFamily || 'Arial';
                  const fontSpec = `${fs} ${fw} ${t.fontSize}px "${ff}"`;
                  const fontsApi = (document as unknown as { fonts?: { check: (s: string) => boolean } }).fonts;
                  const faceReady = fontsApi ? fontsApi.check(fontSpec) : false;
                  if (faceReady && typeof document !== 'undefined') {
                    const c = document.createElement('canvas');
                    const cctx = c.getContext('2d');
                    if (cctx) {
                      cctx.font = fontSpec;
                      let advance = 0;
                      for (const line of String(t.text).split('\n')) {
                        const w = cctx.measureText(line || 'M').width;
                        if (w > advance) advance = w;
                      }
                      if (advance > 0) {
                        const lastGlyph = String(t.text).trimEnd().slice(-1);
                        const punctBump = lastGlyph && /[.,!?;:"')\]}]/.test(lastGlyph) ? t.fontSize * 0.15 : 0;
                        let minWidth: number;
                        if (next.type === 'button') {
                          const paddingX = typeof t.paddingX === 'number' ? t.paddingX : Math.max(t.fontSize * 0.75, 12);
                          const borderW = t.border?.width ?? 0;
                          minWidth = advance + paddingX * 2 + borderW * 2 + punctBump;
                        } else {
                          minWidth = advance + Math.max(t.fontSize * 0.08, 3) + punctBump;
                        }
                        const stored = t.width ?? 0;
                        if (minWidth > stored + 0.5) {
                          next = { ...next, width: Math.ceil(minWidth) } as typeof el;
                        }
                      }
                    }
                  }
                } catch {
                  // measurement unavailable; leave width alone
                }
              }
            }
            if (next.type === 'button') console.log('[Phase F.1.2] setActiveProject post-repair button', { id: next.id, width: next.width, fontSize: (next as { fontSize?: number }).fontSize, fontFamily: (next as { fontFamily?: string }).fontFamily, paddingX: (next as { paddingX?: number }).paddingX, text: (next as { text?: string }).text });
            return next;
          }),
        }
      : rawCs;
    set({
      activeProject: project,
      canvasState: cs,
      selectedElementId: null,
      selectedElementIds: [],
      undoStack: [],
      redoStack: [],
      _lastSavedElementCount: cs.elements.length,
      _autoSaveTimer: null,
      _thumbnailTimer: null,
      saveStatus: 'idle',
      lastSavedAt: null,
      saveError: null,
      _isSaving: false,
    });

    // Load (and migrate inline dynamicField into) persisted element mappings for this project.
    // `cs_element_mappings` is the single store-of-record for dynamic bindings; after migration
    // we strip any leftover inline `dynamicField` from the canvas elements so PropertyPanel and
    // LayerPanel agree on dynamic-vs-static state.
    const mappingStore = useMappingStore.getState();
    if (project?.id) {
      mappingStore.loadMappingsForProject(project.id, cs.elements).then((migratedIds) => {
        const current = get();
        if (current.activeProject?.id !== project.id) return;
        const migratedSet = new Set(migratedIds || []);
        const latestMappings = useMappingStore.getState().elementMappings;
        let changed = false;
        const repaired = current.canvasState.elements.map((el) => {
          const hasMapping = (latestMappings[el.id]?.length ?? 0) > 0;
          const needsFlipToDynamic = hasMapping && el.contentType === 'STATIC';
          const needsStrip =
            migratedSet.has(el.id) && 'dynamicField' in (el as Record<string, unknown>);
          if (!needsFlipToDynamic && !needsStrip) return el;
          changed = true;
          let next: typeof el = el;
          if (needsStrip) {
            const { dynamicField: _dropped, ...rest } = next as unknown as Record<string, unknown>;
            void _dropped;
            next = rest as typeof el;
          }
          if (needsFlipToDynamic) {
            next = { ...next, contentType: 'DYNAMIC' } as typeof el;
          }
          return next;
        });
        if (!changed) return;
        set({ canvasState: { ...current.canvasState, elements: repaired } });
        current._scheduleAutoSave?.();
      });
    } else {
      mappingStore.loadMappingsForProject(null);
    }
  },

  loadFullProject: async (id) => {
    try {
      const { data, error } = await supabase
        .from('cs_projects')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) {
        console.error('[Studio] Error loading full project:', error);
        return null;
      }

      return (data as Project) || null;
    } catch (err) {
      console.error('[Studio] Exception loading full project:', err);
      return null;
    }
  },

  // ═══════════════════════════════════════
  // Canvas Actions
  // ═══════════════════════════════════════

  setCanvasState: (canvasState) => {
    set({ canvasState });
    get()._scheduleAutoSave();
    get()._scheduleThumbnailGeneration();
  },

  setCanvasDimensions: (width, height) => {
    const current = get().canvasState;
    if (current.dimensions.width === width && current.dimensions.height === height) {
      return;
    }
    set({
      canvasState: {
        ...current,
        dimensions: { width, height },
      },
    });
    get()._scheduleAutoSave();
    get()._scheduleThumbnailGeneration();
  },

  addElement: (element) => {
    get().pushUndoState();
    set((state) => ({
      canvasState: {
        ...state.canvasState,
        elements: [...state.canvasState.elements, element],
      },
      selectedElementId: element.id,
      selectedElementIds: [element.id],
      redoStack: [],
    }));
    get()._scheduleAutoSave();
    get()._scheduleThumbnailGeneration();
  },

  updateElement: (id, updates) => {
    get().pushUndoState();
    set((state) => ({
      canvasState: {
        ...state.canvasState,
        elements: state.canvasState.elements.map((el) => {
          if (el.id !== id) return el;
          const merged = { ...el, ...updates } as CanvasElement;

          if (el.type === 'text' || el.type === 'button') {
            const nextText =
              'text' in updates ? (updates as any).text : (el as any).text;
            (merged as any).name = deriveTextElementName(nextText || '');
          }

          return merged;
        }),
      },
      redoStack: [],
    }));
    get()._scheduleAutoSave();
    get()._scheduleThumbnailGeneration();
  },

  updateElements: (updates) => {
    if (updates.length === 0) return;
    get().pushUndoState();
    set((state) => {
      const newElements = state.canvasState.elements.map((el) => {
        const upd = updates.find((u) => u.id === el.id);
        if (!upd) return el;
        const merged = { ...el, ...upd.changes } as CanvasElement;

        if (el.type === 'text' || el.type === 'button') {
          const nextText =
            'text' in upd.changes ? (upd.changes as any).text : (el as any).text;
          (merged as any).name = deriveTextElementName(nextText || '');
        }

        return merged;
      });

      return {
        canvasState: {
          ...state.canvasState,
          elements: newElements,
        },
        redoStack: [],
      };
    });
    get()._scheduleAutoSave();
    get()._scheduleThumbnailGeneration();
  },

  removeElement: (id) => {
    get().pushUndoState();
    set((state) => {
      const newIds = state.selectedElementIds.filter((eid) => eid !== id);
      const filtered = state.canvasState.elements.filter((el) => el.id !== id);
      const pruned = pruneChildIdFromGroups(filtered, id);
      const detached = pruned.map((el) => {
        if (el.type === 'text' && (el as any).textPath?.pathElementId === id) {
          const { textPath: _, ...rest } = el as any;
          return rest;
        }
        return el;
      });
      return {
        canvasState: {
          ...state.canvasState,
          elements: detached,
        },
        selectedElementId: state.selectedElementId === id ? (newIds[0] || null) : state.selectedElementId,
        selectedElementIds: newIds,
        redoStack: [],
      };
    });
    useMappingStore.getState().removeAllMappings(id);
    get()._scheduleAutoSave();
    get()._scheduleThumbnailGeneration();
  },

  selectElement: (id) => {
    set({ selectedElementId: id, selectedElementIds: id ? [id] : [] });
  },

  setSelectedElements: (ids) => {
    set({ selectedElementIds: ids, selectedElementId: ids[0] || null });
  },

  addToSelection: (id) => {
    set((state) => {
      if (state.selectedElementIds.includes(id)) return state;
      const newIds = [...state.selectedElementIds, id];
      return { selectedElementIds: newIds, selectedElementId: newIds[0] || null };
    });
  },

  removeFromSelection: (id) => {
    set((state) => {
      const newIds = state.selectedElementIds.filter((eid) => eid !== id);
      return { selectedElementIds: newIds, selectedElementId: newIds[0] || null };
    });
  },

  clearSelection: () => {
    set({ selectedElementIds: [], selectedElementId: null });
  },

  getSelectedElements: () => {
    const state = get();
    return state.canvasState.elements.filter((el) => state.selectedElementIds.includes(el.id));
  },

  duplicateElement: (id) => {
    const { canvasState } = get();
    const element = canvasState.elements.find((el) => el.id === id);
    if (!element) return;

    get().pushUndoState();

    const subtree = expandSubtree(canvasState.elements, [id]);
    const { elements: clonedSubtree, idMap } = cloneSubtreeWithNewIds(subtree);
    const rootNewId = idMap.get(id)!;

    // Offset only the root; descendants inherit the shift through the group transform.
    const shifted = clonedSubtree.map((el) => {
      if (el.id !== rootNewId) return el;
      // Text/button names always mirror their text content; the "(copy)"
      // suffix is a short-lived disambiguator that disappears as soon as the
      // copy's text is edited (updateElement re-derives from text).
      const nextName =
        el.type === 'text' || el.type === 'button'
          ? `${deriveTextElementName(((el as unknown as { text?: string }).text) || '')} (copy)`
          : `${el.name} (copy)`;
      return {
        ...el,
        x: el.x + 20,
        y: el.y + 20,
        name: nextName,
      };
    });

    set((state) => ({
      canvasState: {
        ...state.canvasState,
        elements: [...state.canvasState.elements, ...shifted],
      },
      selectedElementId: rootNewId,
      selectedElementIds: [rootNewId],
      redoStack: [],
    }));

    // Carry mappings for every element we cloned, not just the root.
    const mappingStore = useMappingStore.getState();
    for (const [oldId, newId] of idMap) {
      const sourceMappings = mappingStore.elementMappings[oldId] || [];
      for (const m of sourceMappings) {
        mappingStore.addMapping(newId, {
          source: m.source,
          tableName: m.tableName,
          columnName: m.columnName,
          columnDataType: m.columnDataType,
          targetProperty: m.targetProperty,
          fallbackValue: m.fallbackValue,
          transformExpression: m.transformExpression,
          filterColumn: m.filterColumn ?? null,
          filterValue: m.filterValue ?? null,
          filterCategory: m.filterCategory ?? null,
        });
      }
    }

    get()._scheduleAutoSave();
    get()._scheduleThumbnailGeneration();
  },

  reorderElement: (id, direction) => {
    get().pushUndoState();
    set((state) => {
      const elements = [...state.canvasState.elements];
      const index = elements.findIndex((el) => el.id === id);
      if (index === -1) return state;

      let newIndex: number;
      switch (direction) {
        case 'up':
          newIndex = Math.min(index + 1, elements.length - 1);
          break;
        case 'down':
          newIndex = Math.max(index - 1, 0);
          break;
        case 'top':
          newIndex = elements.length - 1;
          break;
        case 'bottom':
          newIndex = 0;
          break;
        default:
          return state;
      }

      if (newIndex === index) return state;

      const [removed] = elements.splice(index, 1);
      elements.splice(newIndex, 0, removed);

      const reindexed = elements.map((el, i) => ({ ...el, zIndex: i }));

      return {
        canvasState: {
          ...state.canvasState,
          elements: reindexed as CanvasElement[],
        },
        redoStack: [],
      };
    });
    get()._scheduleAutoSave();
    get()._scheduleThumbnailGeneration();
  },

  reorderElements: (ids, direction) => {
    get().pushUndoState();
    set((state) => {
      const elements = [...state.canvasState.elements];
      const selectedSet = new Set(ids);
      const selected = elements.filter((el) => selectedSet.has(el.id));
      const unselected = elements.filter((el) => !selectedSet.has(el.id));

      let newOrder: typeof elements;
      switch (direction) {
        case 'front':
          newOrder = [...unselected, ...selected];
          break;
        case 'back':
          newOrder = [...selected, ...unselected];
          break;
        case 'forward': {
          newOrder = [...elements];
          const indices = newOrder
            .map((el, i) => selectedSet.has(el.id) ? i : -1)
            .filter((i) => i >= 0)
            .reverse();
          for (const idx of indices) {
            if (idx < newOrder.length - 1 && !selectedSet.has(newOrder[idx + 1].id)) {
              [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
            }
          }
          break;
        }
        case 'backward': {
          newOrder = [...elements];
          const indices = newOrder
            .map((el, i) => selectedSet.has(el.id) ? i : -1)
            .filter((i) => i >= 0);
          for (const idx of indices) {
            if (idx > 0 && !selectedSet.has(newOrder[idx - 1].id)) {
              [newOrder[idx], newOrder[idx - 1]] = [newOrder[idx - 1], newOrder[idx]];
            }
          }
          break;
        }
      }

      const reindexed = newOrder.map((el, i) => ({ ...el, zIndex: i }));
      return {
        canvasState: { ...state.canvasState, elements: reindexed as CanvasElement[] },
        redoStack: [],
      };
    });
    get()._scheduleAutoSave();
    get()._scheduleThumbnailGeneration();
  },

  groupSelected: () => {
    console.log('[GROUP-ENTRY-STORE]', {
      stage: 'groupSelected-invoked',
      timestamp: Date.now(),
      selectedElementIds: get().selectedElementIds,
      selectedCount: get().selectedElementIds.length,
    });

    // Capture selection BEFORE discardActiveObject fires selection:cleared,
    // which synchronously clears selectedElementIds via the canvas event handler.
    const capturedIds = [...get().selectedElementIds];

    const fcForGrouping = useCanvasStore.getState().fabricCanvas;
    if (fcForGrouping) {
      fcForGrouping.discardActiveObject();
      fcForGrouping.requestRenderAll();
    }

    // Read canvasState AFTER discard so element data is fresh if any
    // event handler wrote to the store during the discard cascade.
    const { canvasState } = get();
    if (capturedIds.length < 2) return;

    const selectedElements = canvasState.elements.filter((el) => capturedIds.includes(el.id));
    if (selectedElements.length < 2) return;

    get().pushUndoState();

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const el of selectedElements) {
      minX = Math.min(minX, el.x);
      minY = Math.min(minY, el.y);
      maxX = Math.max(maxX, el.x + el.width);
      maxY = Math.max(maxY, el.y + el.height);
    }

    const groupId = crypto.randomUUID();
    const groupElement: GroupElement = {
      id: groupId,
      type: 'group',
      name: 'Group',
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      rotation: 0,
      opacity: 1,
      visible: true,
      locked: false,
      zIndex: canvasState.elements.length,
      layer: 'BACKGROUND_LAYER',
      contentType: 'STATIC',
      childIds: selectedElements.map((el) => el.id),
    };

    const newElements = [...canvasState.elements, groupElement] as CanvasElement[];

    // DIAGNOSTIC — temporary
    console.log('[GROUP-DEBUG-STORE]', {
      stage: 'groupSelected-before-set',
      capturedIds,
      selectedElementsInput: selectedElements.map((el) => ({
        id: el.id,
        name: el.name,
        type: el.type,
        x: el.x,
        y: el.y,
        width: el.width,
        height: el.height,
      })),
      computedBounds: { minX, minY, maxX, maxY },
      computedGroupSize: { width: maxX - minX, height: maxY - minY },
      newGroupElement: groupElement,
      childrenAfterGrouping: selectedElements.map((el) => ({
        id: el.id,
        x_unchanged: el.x,
        y_unchanged: el.y,
      })),
    });
    // END DIAGNOSTIC

    set({
      canvasState: { ...canvasState, elements: newElements },
      selectedElementId: groupId,
      selectedElementIds: [groupId],
      redoStack: [],
    });
    get()._scheduleAutoSave();
    get()._scheduleThumbnailGeneration();
  },

  ungroupSelected: () => {
    console.log('[UNGROUP-ENTRY-STORE]', {
      stage: 'ungroupSelected-invoked',
      timestamp: Date.now(),
      selectedElementIds: get().selectedElementIds,
      selectedCount: get().selectedElementIds.length,
    });

    // Capture selection BEFORE discardActiveObject fires selection:cleared.
    const capturedIds = [...get().selectedElementIds];

    const fcForUngrouping = useCanvasStore.getState().fabricCanvas;
    if (fcForUngrouping) {
      fcForUngrouping.discardActiveObject();
      fcForUngrouping.requestRenderAll();
    }

    const { canvasState } = get();
    if (capturedIds.length !== 1) return;

    const groupElement = canvasState.elements.find(
      (el) => el.id === capturedIds[0] && el.type === 'group'
    );
    if (!groupElement || groupElement.type !== 'group') return;

    get().pushUndoState();

    const group = groupElement as GroupElement;
    const newElements = canvasState.elements.filter((el) => el.id !== group.id) as CanvasElement[];

    set({
      canvasState: { ...canvasState, elements: newElements },
      selectedElementId: group.childIds[0] || null,
      selectedElementIds: group.childIds,
      redoStack: [],
    });
    get()._scheduleAutoSave();
    get()._scheduleThumbnailGeneration();
  },

  mergeSelectedToSvg: () => {
    const state = get();
    const selectedIds = state.selectedElementIds;
    if (selectedIds.length < 2) return;

    const selectedElements = state.canvasState.elements.filter((el) => selectedIds.includes(el.id));
    if (selectedElements.length < 2) return;

    get().pushUndoState();

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const el of selectedElements) {
      minX = Math.min(minX, el.x);
      minY = Math.min(minY, el.y);
      maxX = Math.max(maxX, el.x + el.width);
      maxY = Math.max(maxY, el.y + el.height);
    }

    const groupWidth = maxX - minX;
    const groupHeight = maxY - minY;

    const { elementToSvgString, generateMergedElementName } = require('../lib/svgMerge');
    const svgChildren = selectedElements.map((el: CanvasElement) => elementToSvgString(el, minX, minY)).filter(Boolean);

    const svgMarkup = [
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${Math.round(groupWidth)} ${Math.round(groupHeight)}" width="${Math.round(groupWidth)}" height="${Math.round(groupHeight)}">`,
      ...svgChildren,
      '</svg>',
    ].join('\n');

    const name = generateMergedElementName(selectedElements);

    const newElement: SvgElement = {
      id: crypto.randomUUID(),
      type: 'svg',
      name,
      x: minX,
      y: minY,
      width: groupWidth,
      height: groupHeight,
      rotation: 0,
      opacity: 1,
      visible: true,
      locked: false,
      zIndex: state.canvasState.elements.length,
      layer: selectedElements[0].layer || 'BACKGROUND_LAYER',
      contentType: 'STATIC',
      svgMarkup,
      originalWidth: groupWidth,
      originalHeight: groupHeight,
      preserveAspectRatio: true,
      sourceFileName: `merged_${selectedElements.length}_elements`,
    };

    const selectedIdSet = new Set(selectedIds);
    const firstSelectedIndex = state.canvasState.elements.findIndex((el) => selectedIdSet.has(el.id));
    const remaining = state.canvasState.elements.filter((el) => !selectedIdSet.has(el.id));
    remaining.splice(Math.min(firstSelectedIndex, remaining.length), 0, newElement);

    set({
      canvasState: { ...state.canvasState, elements: remaining as CanvasElement[] },
      selectedElementId: newElement.id,
      selectedElementIds: [newElement.id],
      redoStack: [],
    });
    get()._scheduleAutoSave();
    get()._scheduleThumbnailGeneration();
  },

  explodeSvgElement: async (elementId?: string) => {
    const state = get();
    const targetId = elementId || state.selectedElementIds[0];
    const element = state.canvasState.elements.find((el) => el.id === targetId);
    if (!element || element.type !== 'svg') return;

    const svgEl = element as SvgElement;
    if (!svgEl.svgMarkup) return;

    get().pushUndoState();

    const { parseSvgFileDecomposed } = await import('../lib/svgImport');

    const blob = new Blob([svgEl.svgMarkup], { type: 'image/svg+xml' });
    const file = new File([blob], svgEl.sourceFileName || 'exploded.svg', { type: 'image/svg+xml' });

    const result = await parseSvgFileDecomposed(
      file,
      { width: svgEl.originalWidth || svgEl.width, height: svgEl.originalHeight || svgEl.height },
      0,
      svgEl.layer || 'BACKGROUND_LAYER'
    );

    if (!result.success || !result.elements) return;

    const scaleX = svgEl.width / (svgEl.originalWidth || svgEl.width);
    const scaleY = svgEl.height / (svgEl.originalHeight || svgEl.height);

    const offsetElements = result.elements.map((el) => ({
      ...el,
      id: crypto.randomUUID(),
      x: (el.x || 0) * scaleX + svgEl.x,
      y: (el.y || 0) * scaleY + svgEl.y,
      width: (el.width || 0) * scaleX,
      height: (el.height || 0) * scaleY,
      layer: svgEl.layer || 'BACKGROUND_LAYER',
    })) as CanvasElement[];

    const elementIndex = state.canvasState.elements.findIndex((el) => el.id === targetId);
    const newElements = [...state.canvasState.elements];
    newElements.splice(elementIndex, 1, ...offsetElements);

    set({
      canvasState: { ...state.canvasState, elements: newElements },
      selectedElementIds: offsetElements.map((el) => el.id),
      selectedElementId: offsetElements[0]?.id || null,
      redoStack: [],
    });
    get()._scheduleAutoSave();
    get()._scheduleThumbnailGeneration();
  },

  restoreElements: (elements) => {
    set((state) => ({
      canvasState: { ...state.canvasState, elements },
      selectedElementId: null,
      selectedElementIds: [],
    }));
  },

  setReadingOrder: (order) => {
    set((state) => ({
      canvasState: { ...state.canvasState, readingOrder: order },
    }));
    get()._scheduleAutoSave();
  },

  // ═══════════════════════════════════════
  // Undo / Redo
  // ═══════════════════════════════════════

  pushUndoState: () => {
    set((state) => {
      const stack = [...state.undoStack, structuredClone(state.canvasState)];
      if (stack.length > MAX_UNDO_STACK) {
        stack.shift();
      }
      return { undoStack: stack };
    });
  },

  undo: () => {
    const prev = get().undoStack.length;
    set((state) => {
      if (state.undoStack.length === 0) return state;
      const previous = state.undoStack[state.undoStack.length - 1];
      const survivingIds = state.selectedElementIds.filter((id) =>
        previous.elements.some((el) => el.id === id)
      );
      return {
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, structuredClone(state.canvasState)],
        canvasState: previous,
        selectedElementId: survivingIds[0] || null,
        selectedElementIds: survivingIds,
      };
    });
    if (prev > 0) {
      get()._scheduleAutoSave();
      get()._scheduleThumbnailGeneration();
    }
  },

  redo: () => {
    const prev = get().redoStack.length;
    set((state) => {
      if (state.redoStack.length === 0) return state;
      const next = state.redoStack[state.redoStack.length - 1];
      const survivingIds = state.selectedElementIds.filter((id) =>
        next.elements.some((el) => el.id === id)
      );
      return {
        redoStack: state.redoStack.slice(0, -1),
        undoStack: [...state.undoStack, structuredClone(state.canvasState)],
        canvasState: next,
        selectedElementId: survivingIds[0] || null,
        selectedElementIds: survivingIds,
      };
    });
    if (prev > 0) {
      get()._scheduleAutoSave();
      get()._scheduleThumbnailGeneration();
    }
  },

  // ═══════════════════════════════════════
  // Auto-Save System
  // ═══════════════════════════════════════

  _scheduleAutoSave: () => {
    const state = get();
    if (!state.activeProject) return;
    if (state._autoSaveTimer) clearTimeout(state._autoSaveTimer);
    const timer = setTimeout(() => { get()._executeAutoSave(); }, 3000);
    set({ _autoSaveTimer: timer });
  },

  _executeAutoSave: async () => {
    const state = get();
    if (!state.activeProject) return;
    if (state._isSaving) return;

    const currentCount = state.canvasState.elements.length;
    const lastCount = state._lastSavedElementCount;

    if (currentCount === 0 && lastCount > 0) {
      console.warn(
        '[AutoSave] BLOCKED — Element count dropped from', lastCount,
        'to 0. Possible hydration failure. Skipping save to protect data.'
      );
      set({ saveStatus: 'error', saveError: 'Save skipped — unexpected empty state detected' });
      return;
    }

    const guarded = guardCanvasStateForSave(state.canvasState);
    const payloadSizeKB = guarded.sizeBytes / 1024;

    if (guarded.removedImageSrc > 0 || guarded.removedSvgImages > 0) {
      console.warn(
        `[AutoSave] Stripped inline base64 images before save:`,
        { imageSrc: guarded.removedImageSrc, svgImages: guarded.removedSvgImages }
      );
      set({ canvasState: guarded.state });
    }

    if (guarded.oversize) {
      console.error(`[AutoSave] Canvas state too large (${payloadSizeKB.toFixed(0)}KB). Blocking save.`);
      set({
        saveStatus: 'error',
        saveError: `Project data is too large (${(payloadSizeKB / 1024).toFixed(1)}MB, max ${(MAX_CANVAS_STATE_BYTES / 1024 / 1024).toFixed(0)}MB). Upload images via the Asset Manager.`,
      });
      return;
    }

    set({ _isSaving: true, saveStatus: 'saving', saveError: null });

    try {
      console.log('[AutoSave] Calling cs_save_project_canvas RPC', {
        projectId: state.activeProject.id,
        elementCount: currentCount,
        payloadKB: Math.round(payloadSizeKB),
      });

      const savePromise = supabase.rpc('cs_save_project_canvas', {
        p_project_id: state.activeProject.id,
        p_canvas_state: guarded.state,
        p_element_mappings: serializeMappingsForSave(
          useMappingStore.getState().elementMappings
        ),
      });

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Auto-save timed out after 15 seconds')), 15000)
      );

      const result = await Promise.race([savePromise, timeoutPromise]) as any;

      if (result?.error) {
        console.error('[AutoSave] RPC returned error', {
          message: result.error.message,
          code: result.error.code,
          details: result.error.details,
          hint: result.error.hint,
          status: result.status,
          fullError: result.error,
        });
        throw result.error;
      }

      console.log('[AutoSave] RPC success', { projectId: state.activeProject.id });

      const now = new Date();
      set({
        _isSaving: false,
        saveStatus: 'saved',
        lastSavedAt: now,
        saveError: null,
        _lastSavedElementCount: currentCount,
        _autoSaveTimer: null,
      });

      set((s) => ({
        projects: s.projects.map((p) =>
          p.id === s.activeProject?.id
            ? { ...p, canvas_state: s.canvasState, updated_at: now.toISOString() }
            : p
        ),
        activeProject: s.activeProject
          ? { ...s.activeProject, canvas_state: s.canvasState, updated_at: now.toISOString() }
          : null,
      }));
    } catch (err: any) {
      console.error('[AutoSave] FAILED — full diagnostic:', {
        errorMessage: err?.message,
        errorCode: err?.code,
        errorDetails: err?.details,
        errorHint: err?.hint,
        errorStatus: err?.status,
        errorName: err?.name,
        fullError: err,
        payloadBytes: new Blob([JSON.stringify(get().canvasState)]).size,
        projectId: state.activeProject?.id,
        elementCount: state.canvasState.elements.length,
      });
      const userMessage = err?.hint
        ? `${err.message} (${err.hint})`
        : err?.details
          ? `${err.message}: ${err.details}`
          : err?.message || 'Failed to save';
      set({ _isSaving: false, saveStatus: 'error', saveError: userMessage, _autoSaveTimer: null });
    }
  },

  saveProjectNow: async () => {
    const state = get();
    if (state._autoSaveTimer) {
      clearTimeout(state._autoSaveTimer);
      set({ _autoSaveTimer: null });
    }

    if (state.activeProject?.status === 'draft') {
      try {
        await supabase
          .from('cs_projects')
          .update({ status: 'in_progress' })
          .eq('id', state.activeProject.id);

        set((s) => ({
          activeProject: s.activeProject ? { ...s.activeProject, status: 'in_progress' as const } : null,
          projects: s.projects.map((p) =>
            p.id === s.activeProject?.id ? { ...p, status: 'in_progress' as const } : p
          ),
        }));
      } catch { /* non-critical */ }
    }

    if (!state.activeProject) return;
    if (state._isSaving) return;

    const guarded = guardCanvasStateForSave(state.canvasState);
    const manualPayloadSize = guarded.sizeBytes / 1024;

    if (guarded.removedImageSrc > 0 || guarded.removedSvgImages > 0) {
      console.warn(
        `[ManualSave] Stripped inline base64 images before save:`,
        { imageSrc: guarded.removedImageSrc, svgImages: guarded.removedSvgImages }
      );
      set({ canvasState: guarded.state });
    }

    if (guarded.oversize) {
      console.error(`[ManualSave] Canvas state too large (${manualPayloadSize.toFixed(0)}KB). Blocking save.`);
      set({
        _isSaving: false,
        saveStatus: 'error',
        saveError: `Project data is too large (${(manualPayloadSize / 1024).toFixed(1)}MB, max ${(MAX_CANVAS_STATE_BYTES / 1024 / 1024).toFixed(0)}MB). Upload images via the Asset Manager.`,
      });
      return;
    }

    set({ _isSaving: true, saveStatus: 'saving', saveError: null });

    try {
      console.log('[ManualSave] Calling cs_save_project_canvas RPC', {
        projectId: state.activeProject.id,
        elementCount: state.canvasState.elements.length,
        payloadKB: Math.round(manualPayloadSize),
      });

      const savePromise = supabase.rpc('cs_save_project_canvas', {
        p_project_id: state.activeProject.id,
        p_canvas_state: guarded.state,
        p_element_mappings: serializeMappingsForSave(
          useMappingStore.getState().elementMappings
        ),
      });

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Save timed out after 30 seconds')), 30000)
      );

      const result = await Promise.race([savePromise, timeoutPromise]) as any;

      if (result?.error) {
        console.error('[ManualSave] RPC returned error', {
          message: result.error.message,
          code: result.error.code,
          details: result.error.details,
          hint: result.error.hint,
          status: result.status,
          fullError: result.error,
        });
        throw result.error;
      }

      console.log('[ManualSave] RPC success', { projectId: state.activeProject.id });

      const now = new Date();
      set({
        _isSaving: false,
        saveStatus: 'saved',
        lastSavedAt: now,
        saveError: null,
        _lastSavedElementCount: state.canvasState.elements.length,
        _autoSaveTimer: null,
      });

      set((s) => ({
        projects: s.projects.map((p) =>
          p.id === s.activeProject?.id
            ? { ...p, canvas_state: s.canvasState, updated_at: now.toISOString() }
            : p
        ),
        activeProject: s.activeProject
          ? { ...s.activeProject, canvas_state: s.canvasState, updated_at: now.toISOString() }
          : null,
      }));
    } catch (err: any) {
      console.error('[ManualSave] FAILED — full diagnostic:', {
        errorMessage: err?.message,
        errorCode: err?.code,
        errorDetails: err?.details,
        errorHint: err?.hint,
        errorStatus: err?.status,
        errorName: err?.name,
        fullError: err,
        payloadBytes: new Blob([JSON.stringify(get().canvasState)]).size,
        projectId: state.activeProject?.id,
        elementCount: state.canvasState.elements.length,
      });
      const userMessage = err?.hint
        ? `${err.message} (${err.hint})`
        : err?.details
          ? `${err.message}: ${err.details}`
          : err?.message || 'Save timed out. Project data may be too large.';
      set({ _isSaving: false, saveStatus: 'error', saveError: userMessage, _autoSaveTimer: null });
      return;
    }

    try {
      await get()._generateThumbnail();
    } catch (thumbErr) {
      console.warn('[Save] Thumbnail generation failed (non-blocking):', thumbErr);
    }
  },

  _scheduleThumbnailGeneration: () => {
    // Thumbnails are now generated only on explicit save (saveProjectNow) or
    // when creating a template. This prevents storage churn during heavy edits
    // and keeps thumbnail lifecycle predictable.
  },

  _generateThumbnail: async () => {
    const state = get();
    if (!state.activeProject) {
      set({ thumbnailLastError: 'No active project' });
      return;
    }
    try {
      const { useCanvasStore } = await import('./canvasStore');
      const cs = useCanvasStore.getState();
      const fc = cs.fabricCanvas;
      if (!fc) {
        set({ thumbnailLastError: 'Canvas not mounted' });
        return;
      }
      // Ensure fabric has rendered the current state before rasterizing.
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      const result = await generateAndSaveThumbnail(
        fc, state.activeProject.id, cs.canvasWidth, cs.canvasHeight
      );
      if (result.ok) {
        set((s) => ({
          activeProject: s.activeProject ? { ...s.activeProject, thumbnail_url: result.url } : null,
          projects: s.projects.map((p) =>
            p.id === s.activeProject?.id ? { ...p, thumbnail_url: result.url } : p
          ),
          thumbnailLastError: null,
        }));
      } else {
        console.warn('[Thumbnail] failed:', result.reason);
        set({ thumbnailLastError: result.reason });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      console.warn('[Thumbnail] Generation failed:', err);
      set({ thumbnailLastError: msg });
    }
  },

  flushAndCleanup: () => {
    const state = get();
    if (state._autoSaveTimer) {
      clearTimeout(state._autoSaveTimer);
      get()._executeAutoSave();
    }
    if (state._thumbnailTimer) {
      clearTimeout(state._thumbnailTimer);
    }
    // Release lock best-effort (non-awaited; caller navigates away)
    get().releaseProjectLock().catch(() => {});
    set({
      _autoSaveTimer: null,
      _thumbnailTimer: null,
      saveStatus: 'idle',
      lastSavedAt: null,
      saveError: null,
      _lastSavedElementCount: 0,
      _isSaving: false,
    });
  },

  // ═══════════════════════════════════════
  // Template Actions
  // ═══════════════════════════════════════

  loadTemplates: async () => {
    set({ templatesLoading: true });
    try {
      const { data, error } = await supabase
        .from('cs_templates')
        .select('id, project_id, name, description, dynamic_fields, is_published, version, thumbnail_url, category, tags, usage_count, created_by, created_at, updated_at')
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('[Studio] Error loading templates:', error);
        return;
      }

      set({ templates: (data || []) as Template[] });
    } catch (err) {
      console.error('[Studio] Exception loading templates:', err);
    } finally {
      set({ templatesLoading: false });
    }
  },

  saveAsTemplate: async (name, description) => {
    const { canvasState, activeProject } = get();

    const dynamicFields = canvasState.elements
      .filter((el) => el.contentType === 'DYNAMIC' && 'dynamicField' in el && el.dynamicField)
      .map((el) => (el as any).dynamicField);

    try {
      const { data, error } = await supabase
        .from('cs_templates')
        .insert({
          project_id: activeProject?.id || null,
          name,
          description,
          canvas_state: canvasState,
          dynamic_fields: dynamicFields,
          is_published: false,
          version: 1,
        })
        .select()
        .single();

      if (error) {
        console.error('[Studio] Error saving template:', error);
        return null;
      }

      let template = data as Template;

      try {
        const { useCanvasStore } = await import('./canvasStore');
        const cs = useCanvasStore.getState();
        const fc = cs.fabricCanvas;
        if (fc) {
          const thumbnailUrl = await generateAndSaveTemplateThumbnail(
            fc,
            template.id,
            cs.canvasWidth,
            cs.canvasHeight
          );
          if (thumbnailUrl) {
            template = { ...template, thumbnail_url: thumbnailUrl };
          }
        }
      } catch (thumbErr) {
        console.warn('[Studio] Template thumbnail generation failed (non-blocking):', thumbErr);
      }

      set((state) => ({
        templates: [template, ...state.templates],
        activeTemplate: template,
      }));

      return template;
    } catch (err) {
      console.error('[Studio] Exception saving template:', err);
      return null;
    }
  },

  // ═══════════════════════════════════════
  // Export Actions
  // ═══════════════════════════════════════

  loadExportJobs: async () => {
    set({ exportsLoading: true });
    try {
      const { data, error } = await supabase
        .from('cs_exports')
        .select('id, template_id, status, image_format, image_quality, school_ids, total_schools, completed_schools, failed_schools, started_at, completed_at, created_by, created_at')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[Studio] Error loading exports:', error);
        return;
      }

      set({ exportJobs: (data || []) as ExportJob[] });
    } catch (err) {
      console.error('[Studio] Exception loading exports:', err);
    } finally {
      set({ exportsLoading: false });
    }
  },

  // ═══════════════════════════════════════
  // Integration Actions (Art Director + School Management)
  // ═══════════════════════════════════════

  loadSchools: async () => {
    set({ schoolsLoading: true });
    try {
      const { data, error } = await supabase
        .from('sm_schools')
        .select('id, name, short_name, legacy_id, city, state, region, mascot, primary_color, secondary_color, logo_url, website_url, storefront_url, enrollment, address, zip_code, team_email, is_active, metadata')
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('[Studio] Error loading schools:', error);
        return;
      }

      set({ schools: (data || []) as School[] });
    } catch (err) {
      console.error('[Studio] Exception loading schools:', err);
    } finally {
      set({ schoolsLoading: false });
    }
  },

  loadSchoolBrands: async (schoolIds) => {
    try {
      let query = supabase
        .from('ad_school_brands')
        .select('*');

      if (schoolIds && schoolIds.length > 0) {
        query = query.in('school_id', schoolIds);
      }

      const { data, error } = await query.order('school_id');

      if (error) {
        console.error('[Studio] Error loading school brands:', error);
        return;
      }

      set({ schoolBrands: (data || []) as SchoolBrand[] });
    } catch (err) {
      console.error('[Studio] Exception loading school brands:', err);
    }
  },

  setEditAsBrand: async (brandId) => {
    if (!brandId) {
      set({ editAsBrandId: null, editAsBrandData: null, editAsLoading: false });
      return;
    }
    set({ editAsBrandId: brandId, editAsLoading: true });
    try {
      const { data: brand } = await supabase
        .from('ad_school_brands')
        .select('*')
        .eq('id', brandId)
        .maybeSingle();

      if (!brand) {
        set({ editAsBrandData: null, editAsLoading: false });
        return;
      }

      const dataMap: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(brand)) {
        dataMap[`ad.ad_school_brands.${key}`] = value;
      }

      const { data: school } = await supabase
        .from('sm_schools')
        .select('*')
        .eq('id', brand.school_id)
        .maybeSingle();

      if (school) {
        for (const [key, value] of Object.entries(school)) {
          dataMap[`sm.sm_schools.${key}`] = value;
        }
      }

      const elements = get().canvasState.elements;
      const additionalTables = new Set<string>();
      for (const el of elements) {
        if ('dynamicField' in el && el.dynamicField) {
          const key = `${el.dynamicField.source}.${el.dynamicField.table}`;
          if (key !== 'ad.ad_school_brands' && key !== 'sm.sm_schools') {
            additionalTables.add(key);
          }
        }
      }

      // Also include any tables referenced by persisted element mappings.
      const mappingState = useMappingStore.getState().elementMappings;
      const eavTableKeys = new Set<string>();
      for (const mappings of Object.values(mappingState)) {
        for (const m of mappings) {
          const key = `${m.source}.${m.tableName}`;
          if (key !== 'ad.ad_school_brands' && key !== 'sm.sm_schools') {
            additionalTables.add(key);
          }
          if (m.filterValue && m.filterCategory) {
            eavTableKeys.add(key);
          }
        }
      }

      for (const tableKey of additionalTables) {
        const [source, table] = tableKey.split('.');
        try {
          if (eavTableKeys.has(tableKey)) {
            const { data: eavRows } = await supabase
              .from(table)
              .select('category, attribute_key, attribute_value')
              .eq('school_id', brand.school_id);
            if (eavRows && eavRows.length > 0) {
              for (const row of eavRows as Array<{ category: string; attribute_key: string; attribute_value: unknown }>) {
                dataMap[buildEavBrandDataKey(source, table, row.category, row.attribute_key)] = row.attribute_value;
              }
            }
            continue;
          }

          let query = supabase.from(table).select('*').limit(1);
          if (source === 'ad') {
            query = query.or(`school_brand_id.eq.${brandId},school_id.eq.${brand.school_id}`);
          } else if (source === 'sm') {
            if (table === 'sm_schools') {
              query = query.eq('id', brand.school_id);
            } else {
              query = query.eq('school_id', brand.school_id);
            }
          }
          const { data } = await query.maybeSingle();
          if (data) {
            for (const [key, value] of Object.entries(data)) {
              dataMap[`${source}.${table}.${key}`] = value;
            }
          }
        } catch {
          // skip tables that fail
        }
      }

      set({ editAsBrandData: dataMap });
    } catch (err) {
      console.error('[Studio] Failed to load Edit As brand data:', err);
      set({ editAsBrandData: null });
    } finally {
      set({ editAsLoading: false });
    }
  },

  clearEditAs: () => {
    set({ editAsBrandId: null, editAsBrandData: null, editAsLoading: false });
  },

  getResolvedElementValue: (element, property) => {
    const { editAsBrandData } = get();
    if (!editAsBrandData) return null;

    // Prefer persisted enhanced mappings from the mapping store so that the editor
    // live preview matches what the export engine will render.
    const mappings = useMappingStore.getState().elementMappings[element.id] || [];
    const storeMapping = mappings.find((m) => m.targetProperty === property);
    if (storeMapping) {
      return resolveMappedValueFromBrandData(storeMapping, editAsBrandData);
    }

    // Legacy fallback: inline dynamicField (kept for back-compat during migration).
    if (!('dynamicField' in element) || !element.dynamicField) return null;
    if (element.dynamicField.targetProperty !== property) return null;

    const mapping = element.dynamicField;
    const lookupKey = `${mapping.source}.${mapping.table}.${mapping.field}`;
    const value = editAsBrandData[lookupKey];

    if (value === undefined || value === null) {
      return mapping.fallbackValue ?? null;
    }

    let stringValue = String(value);
    if (mapping.transform) {
      switch (mapping.transform) {
        case 'uppercase': stringValue = stringValue.toUpperCase(); break;
        case 'lowercase': stringValue = stringValue.toLowerCase(); break;
        case 'titlecase': stringValue = stringValue.replace(/\b\w/g, c => c.toUpperCase()); break;
      }
    }

    return stringValue;
  },

  ensureEditAsTable: async (source, tableName) => {
    const { editAsBrandId, editAsBrandData } = get();
    if (!editAsBrandId || !editAsBrandData) return;

    // Determine whether any mapping for this (source, table) is EAV. If so,
    // pivot-fetch all attribute rows; otherwise fall back to the flat
    // single-row fetch. The early-return guard must distinguish flat keys
    // (`{source}.{table}.{col}`) from pivot keys (`{source}.{table}.@...`)
    // because the two shapes are not interchangeable.
    const mappingState = useMappingStore.getState().elementMappings;
    let isEav = false;
    for (const mappings of Object.values(mappingState)) {
      for (const m of mappings) {
        if (m.source === source && m.tableName === tableName && m.filterValue && m.filterCategory) {
          isEav = true;
          break;
        }
      }
      if (isEav) break;
    }

    const flatPrefix = `${source}.${tableName}.`;
    const pivotPrefix = `${source}.${tableName}.@`;
    let hasFlat = false;
    let hasPivot = false;
    for (const key of Object.keys(editAsBrandData)) {
      if (key.startsWith(pivotPrefix)) hasPivot = true;
      else if (key.startsWith(flatPrefix)) hasFlat = true;
    }
    if (isEav ? hasPivot : hasFlat) return;

    try {
      const { data: brand } = await supabase
        .from('ad_school_brands')
        .select('id, school_id')
        .eq('id', editAsBrandId)
        .maybeSingle();
      if (!brand) return;

      if (isEav) {
        const { data: eavRows } = await supabase
          .from(tableName)
          .select('category, attribute_key, attribute_value')
          .eq('school_id', brand.school_id);
        if (!eavRows || eavRows.length === 0) return;

        const current = get().editAsBrandData ?? {};
        const merged: Record<string, unknown> = { ...current };
        for (const row of eavRows as Array<{ category: string; attribute_key: string; attribute_value: unknown }>) {
          merged[buildEavBrandDataKey(source, tableName, row.category, row.attribute_key)] = row.attribute_value;
        }
        set({ editAsBrandData: merged });
        return;
      }

      let query = supabase.from(tableName).select('*').limit(1);
      if (source === 'ad') {
        query = query.or(`school_brand_id.eq.${editAsBrandId},school_id.eq.${brand.school_id}`);
      } else if (source === 'sm') {
        if (tableName === 'sm_schools') {
          query = query.eq('id', brand.school_id);
        } else {
          query = query.eq('school_id', brand.school_id);
        }
      }
      const { data } = await query.maybeSingle();
      if (!data) return;

      const current = get().editAsBrandData ?? {};
      const merged: Record<string, unknown> = { ...current };
      for (const [k, v] of Object.entries(data)) {
        merged[`${source}.${tableName}.${k}`] = v;
      }
      set({ editAsBrandData: merged });
    } catch (err) {
      console.warn('[Studio] ensureEditAsTable failed:', err);
    }
  },
}));
